import React, { createContext, useState, useEffect, useCallback } from 'react';
import discoveryService from '../services/discoveryService';

export const ChannelDiscoveryContext = createContext({
  categories: {},
  selectedChannels: [],
  recentSearches: [],
  toggleChannelSelection: () => { },
  clearSelectedChannels: () => { },
  addSearchToHistory: () => { },
  clearSearchHistory: () => { },
  isChannelSelected: () => false,
  
  selectedCategory: '',
  selectedSubcategory: '',
  selectedRegion: '',
  rankingMethod: 'subscribers',
  selectedTags: [],
  setSelectedCategory: () => {},
  setSelectedSubcategory: () => {},
  setSelectedRegion: () => {},
  setRankingMethod: () => {},
  setSelectedTags: () => {},
  
  categoriesLoaded: false,
  hasCategories: false,

  // Новые свойства для кэширования
  cachedChannels: [],
  cachedFilters: null,
  cacheTimestamp: null,
  setCacheData: () => {},
  clearChannelCache: () => {},
  isCacheValid: () => false,

  loading: false,
  loadingMore: false,
  error: null,
  setLoading: () => {},
  setLoadingMore: () => {},
  setError: () => {},
});

export const ChannelDiscoveryProvider = ({ children }) => {
  const [categories, setCategories] = useState({});
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem('channelSelectedCategory') || '';
  });
  const [selectedSubcategory, setSelectedSubcategory] = useState(() => {
    return localStorage.getItem('channelSelectedSubcategory') || '';
  });
  const [selectedRegion, setSelectedRegion] = useState(() => {
    return localStorage.getItem('channelSelectedRegion') || '';
  });
  const [rankingMethod, setRankingMethod] = useState(() => {
    return localStorage.getItem('channelRankingMethod') || 'subscribers';
  });
  const [selectedTags, setSelectedTags] = useState(() => {
    const saved = localStorage.getItem('channelSelectedTags');
    try {
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Новые состояния для кэширования
  const [cachedChannels, setCachedChannels] = useState([]);
  const [cachedFilters, setCachedFilters] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);

  // Константы для кэширования
  const CACHE_TTL = 4 * 60 * 60 * 1000; // 4 часа
  const CACHE_KEY = 'channelDiscoveryCache';
  const CACHE_FILTERS_KEY = 'channelDiscoveryFilters';
  const CACHE_TIME_KEY = 'channelDiscoveryCacheTime';

  // Функция для создания хеша фильтров
  const createFiltersHash = useCallback((filters) => {
    const filterObj = {
      category: filters.selectedCategory || '',
      subcategory: filters.selectedSubcategory || '',
      region: filters.selectedRegion || '',
      tags: Array.isArray(filters.selectedTags) ? [...filters.selectedTags].sort() : [],
      ranking: filters.rankingMethod || 'subscribers'
    };
    return JSON.stringify(filterObj);
  }, []);

  // Получение текущих фильтров
  const getCurrentFilters = useCallback(() => ({
    selectedCategory,
    selectedSubcategory,
    selectedRegion,
    selectedTags,
    rankingMethod
  }), [selectedCategory, selectedSubcategory, selectedRegion, selectedTags, rankingMethod]);

  // Проверка валидности кэша
  const isCacheValid = useCallback(() => {
    if (!cacheTimestamp || !cachedFilters) {
      return false;
    }

    // Проверяем время жизни
    const now = Date.now();
    if (now - cacheTimestamp > CACHE_TTL) {
      return false;
    }

    // Проверяем совпадение фильтров
    const currentFiltersHash = createFiltersHash(getCurrentFilters());
    const cachedFiltersHash = createFiltersHash(cachedFilters);
    
    return currentFiltersHash === cachedFiltersHash;
  }, [cacheTimestamp, cachedFilters, createFiltersHash, getCurrentFilters]);

  // Сохранение данных в кэш
  const setCacheData = useCallback((channels, hasMore = true, currentPage = 1) => {
    const now = Date.now();
    const currentFilters = getCurrentFilters();
    
    setCachedChannels(channels);
    setCachedFilters(currentFilters);
    setCacheTimestamp(now);

    // Сохраняем в localStorage
    try {
      const cacheData = {
        channels,
        hasMore,
        currentPage,
        timestamp: now,
        filters: currentFilters
      };
      
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
      localStorage.setItem(CACHE_FILTERS_KEY, createFiltersHash(currentFilters));
      localStorage.setItem(CACHE_TIME_KEY, now.toString());
    } catch (error) {
      // Игнорируем ошибки localStorage
      console.warn('Failed to save channel cache to localStorage:', error);
    }
  }, [getCurrentFilters, createFiltersHash]);

  // Очистка кэша
  const clearChannelCache = useCallback(() => {
    setCachedChannels([]);
    setCachedFilters(null);
    setCacheTimestamp(null);

    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(CACHE_FILTERS_KEY);
      localStorage.removeItem(CACHE_TIME_KEY);
    } catch (error) {
      // Игнорируем ошибки localStorage
    }
  }, []);

  // Загрузка кэша из localStorage при инициализации
  useEffect(() => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      const cachedTime = localStorage.getItem(CACHE_TIME_KEY);
      
      if (cachedData && cachedTime) {
        const parsedData = JSON.parse(cachedData);
        const timestamp = parseInt(cachedTime);
        const now = Date.now();
        
        // Проверяем, не устарел ли кэш
        if (now - timestamp <= CACHE_TTL && parsedData.channels && parsedData.filters) {
          setCachedChannels(parsedData.channels);
          setCachedFilters(parsedData.filters);
          setCacheTimestamp(timestamp);
        } else {
          // Кэш устарел, очищаем
          clearChannelCache();
        }
      }
    } catch (error) {
      // При ошибке просто очищаем кэш
      clearChannelCache();
    }
  }, [clearChannelCache]);

  // Очистка кэша при изменении фильтров
  useEffect(() => {
    if (cachedFilters) {
      const currentFiltersHash = createFiltersHash(getCurrentFilters());
      const cachedFiltersHash = createFiltersHash(cachedFilters);
      
      if (currentFiltersHash !== cachedFiltersHash) {
        clearChannelCache();
      }
    }
  }, [selectedCategory, selectedSubcategory, selectedRegion, selectedTags, rankingMethod, cachedFilters, createFiltersHash, getCurrentFilters, clearChannelCache]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cachedCategories = localStorage.getItem('channelCategoriesCache');
        const cacheTimestamp = localStorage.getItem('channelCategoriesCacheTime');
        const currentTime = new Date().getTime();

        if (cachedCategories && cacheTimestamp &&
          (currentTime - parseInt(cacheTimestamp) < 24 * 60 * 60 * 1000)) {
          const parsedCategories = JSON.parse(cachedCategories);
          setCategories(parsedCategories);
          setCategoriesLoaded(true);
          return;
        }

        const categoriesData = await discoveryService.getCategories();
        
        if (categoriesData && typeof categoriesData === 'object') {
          let normalizedCategories = {};
          
          if (categoriesData.content_categories) {
            normalizedCategories = categoriesData;
          } 
          else if (categoriesData.categories) {
            normalizedCategories = {
              content_categories: categoriesData.categories
            };
          }
          else {
            normalizedCategories = {
              content_categories: categoriesData
            };
          }
          
          setCategories(normalizedCategories);
          setCategoriesLoaded(true);

          localStorage.setItem('channelCategoriesCache', JSON.stringify(normalizedCategories));
          localStorage.setItem('channelCategoriesCacheTime', currentTime.toString());
        } else {
          throw new Error('Неверный формат данных категорий');
        }
        
      } catch (error) {
        const cachedCategories = localStorage.getItem('channelCategoriesCache');
        if (cachedCategories) {
          try {
            setCategories(JSON.parse(cachedCategories));
            setCategoriesLoaded(true);
            return;
          } catch (e) {
            // Fallback to default categories
          }
        }
        
        const fallbackCategories = {
          content_categories: {
            'Gaming': { 
              title: 'Игры', 
              subcategories: ['Action Games', 'Strategy Games', 'RPG Games', 'Indie Games'] 
            },
            'Education': { 
              title: 'Образование', 
              subcategories: ['Science', 'Mathematics', 'Programming', 'Languages'] 
            },
            'Technology': { 
              title: 'Технологии', 
              subcategories: ['Reviews', 'Tutorials', 'News', 'Hardware'] 
            },
            'Entertainment': { 
              title: 'Развлечения', 
              subcategories: ['Comedy', 'Music', 'Movies', 'TV Shows'] 
            },
            'News': { 
              title: 'Новости', 
              subcategories: ['Politics', 'World News', 'Business', 'Sports'] 
            },
            'Sports': { 
              title: 'Спорт', 
              subcategories: ['Football', 'Basketball', 'Tennis', 'Fitness'] 
            }
          }
        };
        
        setCategories(fallbackCategories);
        setCategoriesLoaded(true);
      }
    };
    
    loadCategories();
  }, []);

  useEffect(() => {
    try {
      setSelectedChannels([]);

      const savedSelectedChannels = localStorage.getItem('selectedChannels');
      if (savedSelectedChannels) {
        try {
          const parsed = JSON.parse(savedSelectedChannels);
          if (Array.isArray(parsed)) {
            setSelectedChannels(parsed);
          } else {
            localStorage.removeItem('selectedChannels');
          }
        } catch (parseError) {
          localStorage.removeItem('selectedChannels');
        }
      }

      const savedRecentSearches = localStorage.getItem('channelRecentSearches');
      if (savedRecentSearches) {
        try {
          const parsed = JSON.parse(savedRecentSearches);
          if (Array.isArray(parsed)) {
            setRecentSearches(parsed);
          } else {
            localStorage.removeItem('channelRecentSearches');
          }
        } catch (parseError) {
          localStorage.removeItem('channelRecentSearches');
        }
      }
    } catch (error) {
      localStorage.removeItem('selectedChannels');
      localStorage.removeItem('channelRecentSearches');
    }
  }, []);

  const toggleChannelSelection = useCallback((channelId) => {
    if (!channelId) {
      return;
    }
    
    setSelectedChannels(prev => {
      if (!Array.isArray(prev)) {
        prev = [];
      }

      const isSelected = prev.includes(channelId);
      let newSelection;

      if (isSelected) {
        newSelection = prev.filter(id => id !== channelId);
      } else {
        if (prev.length >= 10) {
          return prev;
        }
        
        newSelection = [...prev, channelId];
      }

      try {
        localStorage.setItem('selectedChannels', JSON.stringify(newSelection));
      } catch (error) {
        // Error saving to localStorage
      }

      return newSelection;
    });
  }, []);

  const clearSelectedChannels = useCallback(() => {
    setSelectedChannels([]);
    try {
      localStorage.removeItem('selectedChannels');
    } catch (error) {
      // Error removing from localStorage
    }
  }, []);

  const addSearchToHistory = useCallback((query) => {
    if (!query || typeof query !== 'string' || !query.trim()) {
      return;
    }

    const trimmedQuery = query.trim();
    
    setRecentSearches(prev => {
      const filtered = prev.filter(item => item !== trimmedQuery);
      const newSearches = [trimmedQuery, ...filtered].slice(0, 10);

      try {
        localStorage.setItem('channelRecentSearches', JSON.stringify(newSearches));
      } catch (error) {
        // Error saving to localStorage
      }

      return newSearches;
    });
  }, []);

  const clearSearchHistory = useCallback(() => {
    setRecentSearches([]);
    try {
      localStorage.removeItem('channelRecentSearches');
    } catch (error) {
      // Error removing from localStorage
    }
  }, []);

  const isChannelSelected = useCallback((id) => {
    return Array.isArray(selectedChannels) && selectedChannels.includes(id);
  }, [selectedChannels]);

  const setSelectedCategoryWithStorage = useCallback((category) => {
    setSelectedCategory(category);
    if (category) {
      localStorage.setItem('channelSelectedCategory', category);
    } else {
      localStorage.removeItem('channelSelectedCategory');
    }
    setSelectedSubcategory('');
    localStorage.removeItem('channelSelectedSubcategory');
  }, []);

  const setSelectedSubcategoryWithStorage = useCallback((subcategory) => {
    setSelectedSubcategory(subcategory);
    if (subcategory) {
      localStorage.setItem('channelSelectedSubcategory', subcategory);
    } else {
      localStorage.removeItem('channelSelectedSubcategory');
    }
  }, []);

  const setSelectedRegionWithStorage = useCallback((region) => {
    setSelectedRegion(region);
    if (region) {
      localStorage.setItem('channelSelectedRegion', region);
    } else {
      localStorage.removeItem('channelSelectedRegion');
    }
  }, []);

  const setRankingMethodWithStorage = useCallback((method) => {
    setRankingMethod(method);
    if (method) {
      localStorage.setItem('channelRankingMethod', method);
    }
  }, []);

  const setSelectedTagsWithStorage = useCallback((tags) => {
    setSelectedTags(tags);
    localStorage.setItem('channelSelectedTags', JSON.stringify(tags));
  }, []);

  const value = {
    categories,
    selectedChannels,
    recentSearches,
    toggleChannelSelection,
    clearSelectedChannels,
    addSearchToHistory,
    clearSearchHistory,
    isChannelSelected,
    
    selectedCategory,
    selectedSubcategory,
    selectedRegion,
    rankingMethod,
    selectedTags,
    setSelectedCategory: setSelectedCategoryWithStorage,
    setSelectedSubcategory: setSelectedSubcategoryWithStorage, 
    setSelectedRegion: setSelectedRegionWithStorage,
    setRankingMethod: setRankingMethodWithStorage,
    setSelectedTags: setSelectedTagsWithStorage,

    // Новые методы для работы с кэшем
    cachedChannels,
    cachedFilters,
    cacheTimestamp,
    setCacheData,
    clearChannelCache,
    isCacheValid,
    
    loading,
    loadingMore,
    error,
    setLoading,
    setLoadingMore,
    setError,
    
    selectedChannelsCount: selectedChannels.length,
    hasSelectedChannels: selectedChannels.length > 0,
    canSelectMore: selectedChannels.length < 10,
    hasCategories: Object.keys(categories).length > 0,
    categoriesLoaded
  };

  return (
    <ChannelDiscoveryContext.Provider value={value}>
      {children}
    </ChannelDiscoveryContext.Provider>
  );
};

export default ChannelDiscoveryContext;